import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy, siteCopy } from './Slide3Urgency.copy'

const ease = [0.16, 1, 0.3, 1] as const

const STEP_DURATION = 1800
const STEP_DONE = 4
type Step = 0 | 1 | 2 | 3 | 4
type Section = 1 | 2 | 3

function spotlight(section: Section, step: Step) {
  if (step === 0 || step === STEP_DONE) return 1
  return step === section ? 1 : 0.25
}

function highlightBg(section: Section, step: Step) {
  return step === section
    ? 'rgba(254, 240, 138, 0.7)'
    : 'rgba(254, 240, 138, 0)'
}

export default function Slide3Urgency({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy, {
    site: siteCopy,
    // brandToken is a lowercase brand fragment used inside the URL, don't
    // run it through the spelling dictionary (the dictionary is whole-word so
    // tokens like "chase" / "hsbc" pass through anyway, but be explicit).
    skipSpellingFor: ['brandToken', 'urlBefore', 'urlAfter'],
  })
  const [step, setStep] = useState<Step>(0)

  const SECTIONS = [
    { key: 'trust', label: c.trustLabel, items: [c.trustItem], num: 1 as Section },
    { key: 'emotion', label: c.emotionLabel, items: [c.pressureFear, c.pressureUrgency], num: 2 as Section },
    { key: 'action', label: c.actionLabel, items: [c.actionItem], num: 3 as Section },
  ]

  const handleReveal = () => {
    if (step !== 0) return
    setStep(1)
    onComplete()
  }

  useEffect(() => {
    if (step === 0 || step >= STEP_DONE) return
    const t = setTimeout(() => setStep((s) => (s + 1) as Step), STEP_DURATION)
    return () => clearTimeout(t)
  }, [step])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 max-w-4xl mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8 w-full text-start"
      >
        {c.title}
      </motion.h2>

      {/* Horizontal layout: message left, pattern right */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-[3fr_auto_3fr] gap-6 sm:gap-8 items-center">
        {/* SMS bubble */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease }}
        >
          <div className="relative rounded-2xl bg-[#e9e9eb] px-5 py-4 text-start">
            <div className="absolute -bottom-2 start-5 w-4 h-4 bg-[#e9e9eb] rotate-45 rounded-sm" />
            <p className="text-base sm:text-lg text-slate-900 leading-relaxed">
              <motion.span
                initial={false}
                animate={{ opacity: spotlight(1, step), backgroundColor: highlightBg(1, step) }}
                transition={{ duration: 0.6, ease }}
                className="font-semibold rounded [box-decoration-break:clone]"
              >
                {c.sender}
              </motion.span>{' '}
              <motion.span
                initial={false}
                animate={{ opacity: spotlight(2, step), backgroundColor: highlightBg(2, step) }}
                transition={{ duration: 0.6, ease }}
                className="rounded [box-decoration-break:clone]"
              >
                {c.threatBody}
              </motion.span>
            </p>
            <p className="mt-2 text-base text-blue-600 underline">
              <motion.span
                initial={false}
                animate={{ opacity: spotlight(3, step), backgroundColor: highlightBg(3, step) }}
                transition={{ duration: 0.6, ease }}
                className="rounded [box-decoration-break:clone]"
              >
                {c.urlBefore}
              </motion.span>
              <motion.span
                initial={false}
                animate={{
                  opacity: step === 0 || step === STEP_DONE ? 1 : step === 1 || step === 3 ? 1 : 0.25,
                  backgroundColor: step === 1 || step === 3 ? 'rgba(254, 240, 138, 0.7)' : 'rgba(254, 240, 138, 0)',
                }}
                transition={{ duration: 0.6, ease }}
                className="rounded [box-decoration-break:clone]"
              >
                {c.brandToken}
              </motion.span>
              <motion.span
                initial={false}
                animate={{ opacity: spotlight(3, step), backgroundColor: highlightBg(3, step) }}
                transition={{ duration: 0.6, ease }}
                className="rounded [box-decoration-break:clone]"
              >
                {c.urlAfter}
              </motion.span>
            </p>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div
          animate={{ opacity: step >= 1 ? 1 : 0 }}
          transition={{ duration: 0.4, delay: step >= 1 ? 0.15 : 0 }}
          className="hidden sm:flex items-center justify-center"
        >
          <svg width="32" height="24" viewBox="0 0 32 24" fill="none" className="text-slate-300 dir-flip">
            <path d="M0 12h24m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>

        {/* Right side: button or pattern breakdown */}
        <div className="flex items-center">
          {step === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7, ease }}
              className="w-full flex justify-center sm:justify-start"
            >
              <button
                onClick={handleReveal}
                className="rounded-full border border-slate-200 px-8 py-3.5 text-lg font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer"
              >
                {c.revealButton}
              </button>
            </motion.div>
          ) : (
            <div className="text-start space-y-7 w-full">
              {SECTIONS.map((s) => {
                const isVisible = step >= s.num
                const isActive = step === s.num
                const targetOpacity = step === STEP_DONE ? 1 : isActive ? 1 : isVisible ? 0.5 : 0
                return (
                  <motion.div
                    key={s.key}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: targetOpacity, x: isVisible ? 0 : 12 }}
                    transition={{ duration: 0.6, ease }}
                  >
                    <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                      {s.num}. {s.label}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {s.items.map((item, i) => (
                        <li key={i} className="text-base sm:text-lg text-slate-500 leading-snug">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
